import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useDomain } from '../context/DomainContext';
import { useImport } from '../context/ImportContext';
import { useFeatureGate } from '../hooks/useFeatureGate';
import { generateActionItems } from '../actionEngine/generateActionItems';
import { buildActionEngineSummary } from '../reporting/actionEvidenceFormatter';
import { fmtK$ } from '../actionEngine/financialCalculations';
import { ActionItem } from '../actionEngine/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface SeverityCounts { critical: number; high: number; medium: number }

function countBySeverity(items: ActionItem[]): SeverityCounts {
  const open = items.filter(i => i.status === 'open' || i.status === 'in_review');
  return {
    critical: open.filter(i => i.severity === 'critical').length,
    high:     open.filter(i => i.severity === 'high').length,
    medium:   open.filter(i => i.severity === 'medium').length,
  };
}

function VDivider() {
  return (
    <div style={{
      width: 1, height: 40,
      background: 'rgba(34,197,94,0.25)',
      flexShrink: 0,
    }} />
  );
}

function CountPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 38 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{
        fontSize: 10, color: 'var(--text-muted)', marginTop: 2,
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ActionItemsSummaryBanner() {
  const { employees, deptSpend } = useDomain();
  const { isUsingImport }        = useImport();
  const canSeeDetail             = useFeatureGate('action_engine');
  const navigate                 = useNavigate();
  const { organization }         = useOrganization();

  const actionItems = useMemo(
    () => generateActionItems(employees, deptSpend),
    [employees, deptSpend],
  );

  const summary = useMemo(() => buildActionEngineSummary(actionItems), [actionItems]);
  const counts  = useMemo(() => countBySeverity(actionItems),          [actionItems]);

  const dataLabel = isUsingImport ? 'imported data' : 'demo data';

  function handleUpgrade() {
    fetch('/api/create-checkout-session', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan:        'pro',
        clerkOrgId:  organization?.id ?? '',
        clerkUserId: '',
        successUrl:  window.location.href,
        cancelUrl:   window.location.href,
      }),
    })
      .then(r => r.json())
      .then((data: { url?: string }) => { if (data.url) window.location.href = data.url; })
      .catch(err => console.error('[ActionItemsSummaryBanner] checkout error:', err));
  }

  const bannerBase: React.CSSProperties = {
    marginBottom: 20,
    padding:      '14px 18px',
    background:   'rgba(34,197,94,0.04)',
    border:       '1px solid rgba(34,197,94,0.20)',
    borderLeft:   '3px solid #22c55e',
    borderRadius: 8,
    display:      'flex',
    alignItems:   'center',
    gap:          20,
    flexWrap:     'wrap',
  };

  // ── Empty state ──────────────────────────────────────────────────────────────

  if (summary.openItems === 0) {
    return (
      <div style={{ ...bannerBase, padding: '12px 18px' }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success)' }}>
          ✓ No optimisation opportunities detected this period.
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {isUsingImport
            ? 'Your AI spend is within thresholds — continue monitoring.'
            : 'Import employee and usage data to analyse your organisation\'s AI spend.'}
        </span>
      </div>
    );
  }

  // ── Main banner ──────────────────────────────────────────────────────────────

  const topItem = summary.topOpportunity;

  return (
    <div style={bannerBase}>

      {/* Savings figure */}
      <div style={{ flexShrink: 0 }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#22c55e',
          lineHeight: 1, letterSpacing: '-0.02em',
        }}>
          {fmtK$(summary.totalAnnualSavings)}/yr
        </div>
        <div style={{
          fontSize: 10, color: 'var(--text-muted)', marginTop: 3,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          recoverable · {dataLabel}
        </div>
      </div>

      <VDivider />

      {/* Severity counts */}
      <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
        <CountPill label="Open"   value={summary.openItems}   color="var(--text-primary)" />
        {counts.critical > 0 && (
          <CountPill label="Critical" value={counts.critical} color="#ef4444" />
        )}
        {counts.high > 0 && (
          <CountPill label="High"     value={counts.high}     color="#f59e0b" />
        )}
        {counts.medium > 0 && (
          <CountPill label="Medium"   value={counts.medium}   color="#3b82f6" />
        )}
      </div>

      <VDivider />

      {/* Top item detail (Pro) or upgrade CTA (free) */}
      <div style={{ flex: 1, minWidth: 200 }}>
        {canSeeDetail && topItem ? (
          <>
            <div style={{
              fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4,
            }}>
              Top opportunity
            </div>
            <div style={{
              fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {topItem.title}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-secondary)', marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {topItem.recommendedAction.length > 95
                ? topItem.recommendedAction.slice(0, 95) + '…'
                : topItem.recommendedAction}
            </div>
          </>
        ) : !canSeeDetail ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Upgrade to Pro to see the full breakdown and take action.
            </span>
            <button
              className="btn btn-primary"
              style={{ fontSize: 11, padding: '4px 12px' }}
              onClick={handleUpgrade}
            >
              Upgrade to Pro
            </button>
          </div>
        ) : null}
      </div>

      {/* View all — only shown to Pro users (free CTA is inside the middle section) */}
      {canSeeDetail && (
        <button
          className="btn btn-ghost"
          style={{ flexShrink: 0, fontSize: 11 }}
          onClick={() => navigate('/exceptions?tab=actions')}
        >
          View all action items →
        </button>
      )}
    </div>
  );
}
